/**
=========================================================
* Otis Admin PRO - v2.0.1
=========================================================

* Product Page: https://material-ui.com/store/items/otis-admin-pro-material-dashboard-react/
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Otis Admin PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Data
import { Box, Stack, styled } from "@mui/material";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import { isEmpty, set } from "lodash";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import generatePDF, { Margin, usePDF } from "react-to-pdf";
import { useFlatInject } from "store";
import { redirectUrlAfterSaveSection, submitAutoSave } from "utils/common";
import { useAutosave, useHttp } from "utils/hooks";
import { Message } from "utils/message";
import ModalUpdate from "components/Modal";
import MDInput from "components/MDInput";
import { Form, FormItem, useForm } from "../../../../../components/Form";
import UpgradeFromTermSheet from "../TermSheetupgrade/TermSheetContent";
import FinalTermSheetContent from "./TermSheetContent";

const StyledTermSheetContent = styled(FinalTermSheetContent)`
  display: none;
  @media print {
    display: ${({ canPrint = false }) => (canPrint ? "block" : "none")} !important;
    padding: 0;
  }
`;

const initValue = () => ({
  date: new Date(),
  between:
    "<Buyer Company Full Name>, (<Buyer Company Shorthand Name>) a corporation organized and existing under the law of <Buyer Company Geo location>, with its head office address located at: <Buyer Company Address>",
  sellerCompanyName: "",
  sellerCompanyNameShort: "",
  theirAddress: "",
  yourCompanyName: "",
  yourCompanyNameShort: "",
  yourCompanyLocation: "",
  yourCompanyAddress: "",
  overallTerms: "",
  shares: "shares",
  otherShares: "",
  numberOfBuyerSigns: 1,
  numberOfSellerSigns: 1,
});

/**
 * Final term sheet or term sheet .v2
 */
function FinalTermSheet({ isMobile, modeSavePDF = "edit", setTextAutoSave, setSavedSection }) {
  // basic info preparation
  const [
    {
      finalTermSheet,
      saveFinalTermSheet,
      switchTermSheetVersion,
      dealReview,
      setCardOpen,
      baseInfo,
      sendEmailPdf,
    },
  ] = useFlatInject("dealPlanOperation");
  const [{ canPrint, user, logPrint }] = useFlatInject("user");
  const userVIPstate = user;
  const { toPDF, targetRef } = usePDF({
    filename: `TermSheetV2-${dayjs().format("DD/MM")}.pdf`,
    page: { margin: Margin.MEDIUM },
  });

  const { toPDF: toMobilePDF, targetRef: targetMobileRef } = usePDF({
    filename: `TermSheetV2-${dayjs().format("DD/MM")}.pdf`,
    page: { margin: Margin.MEDIUM },
  });

  const [value, setValue] = useState(
    isEmpty(finalTermSheet)
      ? initValue()
      : {
          ...initValue(),
          ...finalTermSheet,
        }
  );
  const [mode, setMode] = useState("edit");
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    setMode(modeSavePDF);
    setValue({ ...initValue(), ...finalTermSheet });
  }, [modeSavePDF, finalTermSheet]);

  const form = useForm({
    initialValues: {
      text: finalTermSheet,
    },
    onSubmit: async (formData) => {
      await saveFinalTermSheet(formData.text);
      Message.success("Changes saved");
    },
  });

  const sendEmailForm = useForm({
    initialValues: {
      email: "",
      sellerCompanyName: finalTermSheet?.sellerCompanyName || "",
      partnerName: dealReview?.contactName || "",
      yourFullName: user?.name,
      yourCompany: finalTermSheet?.yourCompanyName,
    },
  });

  const location = useLocation();
  const navigate = useNavigate();

  const submitForm = async (isAuto = false) => {
    try {
      await saveFinalTermSheet(value);
      if (!isAuto) {
        redirectUrlAfterSaveSection(
          setSavedSection,
          `${location.pathname + location.search}#second-deal-pitch`
        );
        Message.success("Changes saved");
      }
      // setCardOpen("file-upload");
    } catch (error) {
      Message.error(error.message);
    }
  };

  const { run, loading } = useHttp(() => submitForm(false), { manual: true });
  const { run: runAuto } = useHttp(() => submitForm(true), { manual: true });

  useAutosave(async () => {
    if (baseInfo?.id) {
      submitAutoSave(() => runAuto(), setTextAutoSave);
    }
  });

  const onPrintBtnClick = () => {
    switchTermSheetVersion("v2");
    if (!canPrint) {
      Message.warning(
        "Your printing quota has been exhausted. You can utilize this feature by upgrading to a premium package."
      );
      return;
    }
    window.print();
  };

  const onSavePdf = () => {
    const extraButtons = document.getElementsByClassName("extra-buttons-deal-repitch");
    extraButtons.forEach((button) => {
      button.style.display = "none";
    });
    setMode("view");
    switchTermSheetVersion("v2");
    setTimeout(() => {
      isMobile ? toMobilePDF() : toPDF();
      setMode("edit");
    }, 1000);

    setTimeout(() => {
      extraButtons.forEach((button) => {
        button.style.display = "block";
      });
    }, 4000);
  };

  const handleSendEmail = async () => {
    try {
      const extraButtons = document.getElementsByClassName("extra-buttons-deal-repitch");
      extraButtons.forEach((button) => {
        button.style.display = "none";
      });
      setMode("view");
      switchTermSheetVersion("v2");

      setTimeout(async () => {
        const pdf = await generatePDF(targetRef, {
          filename: `TermSheetV2-${dayjs().format("DD/MM")}.pdf`,
          method: "build",
          page: { margin: Margin.MEDIUM },
        });
        setMode("edit");

        const base64 = pdf?.output("datauristring")?.split(",")?.[1];
        const formData = sendEmailForm.values;
        if (
          !formData.email ||
          !formData.partnerName ||
          !formData.yourFullName ||
          !formData.yourCompany ||
          !formData.sellerCompanyName ||
          !base64
        ) {
          Message.error("Please enter all fields");
          return;
        }

        await sendEmailPdf(formData, base64);
        Message.success("Email sent!");
      }, 1000);

      setTimeout(() => {
        extraButtons.forEach((button) => {
          button.style.display = "block";
        });
      }, 4000);
    } catch (er) {
      console.log(er);
      Message.error("Something went wrong!");
    }
  };

  useEffect(() => {
    const afterPrintHandler = (e) => {
      if (canPrint) {
        logPrint();
      }
    };
    window.addEventListener("afterprint", afterPrintHandler);
    return () => window.removeEventListener("afterprint", afterPrintHandler);
  }, [canPrint]);

  // IMPORTANT: className === "no-print" is used to hide the component when printing
  return (
    <Card
      id="final-term-sheet"
      sx={{
        overflow: "visible",
        "@media print": {
          boxShadow: "none",
        },
        boxShadow: isMobile ? "none" : null,
      }}
    >
      <MDBox p={isMobile ? 0 : 3}>
        {user.vip === 0 ? (
          <>
            {!isMobile && (
              <Box display="flex" alignItems="center">
                <MDTypography variant="h4" fontWeight="regular">
                  Term Sheet .v2{" "}
                </MDTypography>
              </Box>
            )}
            <MDBox p={isMobile ? 1 : 3}>
              <Stack
                // direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  direction: {
                    lg: "row",
                    md: "row",
                    sm: "row",
                    xs: "column",
                  },
                }}
              >
                <MDTypography variant="h6" color="secondary">
                  Upgrade to a Deal Maker account to access the Term Sheet
                </MDTypography>
                <UpgradeFromTermSheet />
              </Stack>
            </MDBox>
          </>
        ) : (
          <div>
            <MDBox ref={targetRef}>
              {!isMobile && (
                <Box display="flex" alignItems="center">
                  <MDTypography variant="h4">Term Sheet .v2 </MDTypography>
                </Box>
              )}
              <FinalTermSheetContent
                className="no-print"
                mode={mode}
                value={value}
                onChange={setValue}
                isMobile={isMobile}
              />
            </MDBox>
            <StyledTermSheetContent canPrint={canPrint} value={value} />
            <MDBox pb={3} py={3} className="no-print" mt={3}>
              {!isMobile ? (
                <Grid
                  item
                  xs={12}
                  textAlign="right"
                  sx={{ display: "flex", gap: "8px", justifyContent: "end" }}
                >
                  <MDButton
                    color="green"
                    variant="outlined"
                    onClick={() => navigate("/deal-planner", { replace: true })}
                    sx={{ fontSize: "14px" }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="green"
                    sx={{ fontSize: "14px" }}
                    onClick={() => {
                      switchTermSheetVersion("v2");
                      setTimeout(() => {
                        onPrintBtnClick();
                      }, 4000);
                    }}
                  >
                    Print
                  </MDButton>
                  {/* <MDButton
                    color="green"
                    sx={{ fontSize: "14px" }}
                    onClick={() => {
                      setOpenModal(true);
                    }}
                  >
                    Send Email
                  </MDButton> */}
                  <MDButton color="green" sx={{ fontSize: "14px" }} onClick={onSavePdf}>
                    Save PDF
                  </MDButton>
                  <MDButton
                    color="green"
                    disabled={loading || !dealReview.businessName}
                    loading={loading}
                    onClick={() => run()}
                    sx={{ fontSize: "14px" }}
                  >
                    Save and next
                  </MDButton>
                </Grid>
              ) : (
                <>
                  <Grid
                    item
                    xs={12}
                    textAlign="right"
                    sx={{ display: "flex", gap: "8px", justifyContent: "end" }}
                  >
                    {/* <MDButton
                      color="green"
                      sx={{ fontSize: isMobile ? "14px" : "12px", flex: 1 }}
                      onClick={() => {
                        switchTermSheetVersion("v2");
                        setTimeout(() => {
                          onPrintBtnClick();
                        }, 4000);
                      }}
                    >
                      Print
                    </MDButton> */}

                    {/* <MDButton
                      color="green"
                      variant="outlined"
                      sx={{ fontSize: isMobile ? "14px" : "12px", flex: 1 }}
                      onClick={() => {
                        setOpenModal(true);
                      }}
                    >
                      Send Email
                    </MDButton> */}
                    <MDButton
                      color="green"
                      sx={{ fontSize: isMobile ? "14px" : "12px", flex: 1 }}
                      onClick={onSavePdf}
                    >
                      Save PDF
                    </MDButton>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    textAlign="right"
                    sx={{ display: "flex", gap: "8px", justifyContent: "end", marginTop: "10px" }}
                  >
                    <MDButton
                      color="green"
                      variant="outlined"
                      onClick={() => navigate("/deal-planner", { replace: true })}
                      sx={{ fontSize: isMobile ? "14px" : "12px", flex: 1 }}
                    >
                      Cancel
                    </MDButton>
                    <MDButton
                      color="green"
                      disabled={loading || !dealReview.businessName}
                      loading={loading}
                      onClick={() => run()}
                      sx={{ fontSize: isMobile ? "14px" : "12px", flex: 1 }}
                    >
                      Save and next
                    </MDButton>
                  </Grid>
                  <MDBox
                    ref={targetMobileRef}
                    sx={{
                      position: "absolute",
                      width: "1100px",
                      right: "100000px",
                      overflow: "hidden",
                      height: mode === "view" ? "auto" : "0px",
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <MDTypography variant="h4">Term Sheet .v2 </MDTypography>
                    </Box>
                    <FinalTermSheetContent
                      className="no-print"
                      mode={mode}
                      value={value}
                      onChange={setValue}
                      isMobile={isMobile}
                    />
                  </MDBox>
                </>
              )}
            </MDBox>
          </div>
        )}
      </MDBox>

      {/* <ModalUpdate
        open={openModal}
        setOpen={setOpenModal}
        title="Send Email"
        isSendEmailModal
        content={
          <Grid container spacing={2} sx={{ paddingTop: "20px" }}>
            <Grid item xs={12} md={6}>
              <Form form={sendEmailForm} fieldStyle={{ flex: 1 }} labelSx={{ display: "none" }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormItem
                      as={MDInput}
                      type="text"
                      name="email"
                      variant="standard"
                      fullWidth
                      label="To Email"
                      placeholder="Enter Email"
                      sx={{
                        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormItem
                      as={MDInput}
                      type="text"
                      name="sellerCompanyName"
                      variant="standard"
                      fullWidth
                      label="Seller Company Name"
                      placeholder="Enter Seller Company Name"
                      sx={{
                        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormItem
                      as={MDInput}
                      type="text"
                      name="partnerName"
                      variant="standard"
                      fullWidth
                      label="Partner Name"
                      placeholder="Enter Partner Name"
                      sx={{
                        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormItem
                      as={MDInput}
                      type="text"
                      name="yourFullName"
                      variant="standard"
                      fullWidth
                      label="Your Full Name"
                      placeholder="Enter Your Full Name"
                      sx={{
                        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormItem
                      as={MDInput}
                      type="text"
                      name="yourCompany"
                      variant="standard"
                      fullWidth
                      label="Your Company"
                      placeholder="Enter Your Company"
                      sx={{
                        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Form>
            </Grid>
            <Grid item xs={12} md={6}>
              <div
                style={{
                  margin: 0,
                  padding: "20px",
                  fontFamily: "Arial, sans-serif",
                  backgroundColor: "#f4f4f4",
                  color: "#333",
                }}
              >
                <table
                  width="100%"
                  border="0"
                  cellSpacing="0"
                  cellPadding="0"
                  style={{
                    maxWidth: "600px",
                    margin: "auto",
                    backgroundColor: "#ffffff",
                    border: "1px solid #dddddd",
                    padding: "20px",
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <p style={{ fontSize: "16px", lineHeight: "1.5", color: "#333333" }}>
                          Dear{" "}
                          <span style={{ fontWeight: "bold" }}>
                            {sendEmailForm?.values?.partnerName}
                          </span>
                          ,
                        </p>
                        <br />
                        <p style={{ fontSize: "16px", lineHeight: "1.5", color: "#333333" }}>
                          Further to our recent discussions, I am pleased to attach the Term Sheet
                          pertaining to our agreement for your review.
                        </p>
                        <br />
                        <p style={{ fontSize: "16px", lineHeight: "1.5", color: "#333333" }}>
                          If you have any questions or need any clarification, feel free to reach
                          out to me. When you agree with the Term Sheet, please sign it and return
                          to me.
                        </p>
                        <br />
                        <p style={{ fontSize: "16px", lineHeight: "1.5", color: "#333333" }}>
                          Look forward to hearing from you soon.
                        </p>
                        <br />
                        <p style={{ fontSize: "16px", lineHeight: "1.5", color: "#333333" }}>
                          Best regards,
                        </p>
                        <p
                          style={{
                            fontSize: "16px",
                            lineHeight: "1.5",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          {sendEmailForm?.values?.yourFullName}
                          <br />
                          {sendEmailForm?.values?.yourCompany}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Grid>
          </Grid>
        }
        confirmFunction={handleSendEmail}
        confirmText="Send Email"
        cancelText="Cancel"
        sx={{
          minWidth: {
            xs: "100%",
            md: "50%",
          },
        }}
      /> */}
    </Card>
  );
}

export default FinalTermSheet;
